import { useEffect, useState, useCallback, Suspense } from "react";
import './App.css';
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { PrivacyProvider } from "@/contexts/PrivacyContext";
import { PublicModeProvider } from "@/contexts/PublicModeContext";
import { CallProvider } from "@/contexts/CallContext";
import { lazyRetry } from "@/lib/lazyRetry";

// Lazy-load heavy components to reduce initial bundle and TBT
const CosmicBackground = lazyRetry(() => import("@/components/CosmicBackground").then(m => ({ default: m.CosmicBackground })));
const MainApp = lazyRetry(() => import("@/components/MainApp").then(m => ({ default: m.MainApp })));
const DecoyScreen = lazyRetry(() => import("@/components/DecoyScreen").then(m => ({ default: m.DecoyScreen })));
const UpdateWizardScreen = lazyRetry(() => import("@/components/UpdateWizardScreen").then(m => ({ default: m.UpdateWizardScreen })));
const MaintenanceScreen = lazyRetry(() => import("@/components/MaintenanceScreen").then(m => ({ default: m.MaintenanceScreen })));
const PaymentSuccess = lazyRetry(() => import("./pages/PaymentSuccess"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
import { toast } from "sonner";
import { usePanicSwitch, isPanicSwitchEnabled } from "@/hooks/usePanicSwitch";
import { wasJustUpgraded, isFreshInstall } from "@/lib/updateWizard";
import { checkBackendHealth } from "@/lib/healthCheck";
import { Shield } from "lucide-react";
import { isOfflineMode, setOfflineMode, flushOfflineQueue } from "@/lib/offlineSync";

const queryClient = new QueryClient();

function AppContent() {
  const { showDecoy, dismissDecoy } = usePanicSwitch(isPanicSwitchEnabled());
  const [wizardDone, setWizardDone] = useState(() => !wasJustUpgraded() && !isFreshInstall());
  const handleWizardComplete = useCallback(() => setWizardDone(true), []);

  // Backend health gate — check BEFORE showing auth/unlock
  const [backendStatus, setBackendStatus] = useState<'checking' | 'healthy' | 'down'>('checking');

  useEffect(() => {
    let cancelled = false;
    checkBackendHealth().then(healthy => {
      if (!cancelled) {
        // If offline mode is active, bypass the gate
        if (!healthy && isOfflineMode()) {
          setBackendStatus('healthy');
        } else {
          setBackendStatus(healthy ? 'healthy' : 'down');
        }
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Background polling: when offline mode is active, poll for reconnection
  useEffect(() => {
    if (!isOfflineMode()) return;
    const interval = setInterval(async () => {
      const healthy = await checkBackendHealth();
      if (healthy) {
        const synced = await flushOfflineQueue();
        setOfflineMode(false);
        setBackendStatus('healthy');
        if (synced > 0) {
          toast.success(`Connection restored — ${synced} action${synced > 1 ? 's' : ''} synced ✓`);
        } else {
          toast.success('Connection restored ✓');
        }
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, [backendStatus]);

  // React to offline-mode-change events (from initOfflineDetection)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.offline === false) {
        setBackendStatus('healthy');
      }
    };
    window.addEventListener('offline-mode-change', handler);
    return () => window.removeEventListener('offline-mode-change', handler);
  }, []);

  // Migration is owned EXCLUSIVELY by UpdateWizardScreen below — running it
  // here too caused duplicated cache clears + service-worker churn, which
  // produced the long black screen before the dialog appeared. We only flag
  // a fresh install (no migration needed) and let the wizard handle upgrades.
  useEffect(() => {
    if (isFreshInstall()) {
      try { localStorage.setItem('aphenis_guardian_migration_grace', Date.now().toString()); } catch {}
      setWizardDone(true);
    }
  }, []);

  // Show a minimal loading spinner while checking backend
  if (backendStatus === 'checking') {
    return (
      <div className="h-[100dvh] flex items-center justify-center bg-background overflow-hidden">
        <div className="text-center space-y-4 animate-fade-in">
          <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
            <Shield className="w-7 h-7 text-primary" />
          </div>
          <p className="text-xs text-muted-foreground">Connecting...</p>
        </div>
      </div>
    );
  }

  // Show maintenance screen if backend is down — blocks everything else
  if (backendStatus === 'down') {
    return (
      <Suspense fallback={null}>
        <MaintenanceScreen onRecovered={() => setBackendStatus('healthy')} />
      </Suspense>
    );
  }

  if (!wizardDone && wasJustUpgraded()) {
    return (
      <Suspense fallback={null}>
        <UpdateWizardScreen onComplete={handleWizardComplete} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={
      <div className="h-[100dvh] flex items-center justify-center bg-background">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
          <Shield className="w-7 h-7 text-primary" />
        </div>
      </div>
    }>
      <CosmicBackground />
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MainApp />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      {showDecoy && <DecoyScreen onDismiss={dismissDecoy} />}
    </Suspense>
  );
}

const App = () => {
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      // Suppress non-critical browser rejections (clipboard, ResizeObserver, etc.)
      const msg = reason?.message || reason?.toString?.() || '';
      const suppressPatterns = [
        'Document is not focused',
        'ResizeObserver',
        'Load failed',
        'AbortError',
        'The operation was aborted',
        'The play() request was interrupted',
      ];
      if (suppressPatterns.some(p => msg.includes(p))) {
        console.warn('[Suppressed rejection]', msg);
        event.preventDefault();
        return;
      }
      console.error("Unhandled rejection:", reason);
      event.preventDefault();
    };
    window.addEventListener("unhandledrejection", handleRejection);
    return () => window.removeEventListener("unhandledrejection", handleRejection);
  }, []);

  return (
    <ErrorBoundary label="Something went wrong">
      <QueryClientProvider client={queryClient}>
        <PublicModeProvider>
          <AuthProvider>
            <PrivacyProvider>
              <CallProvider>
                <TooltipProvider>
                  <AppContent />
                </TooltipProvider>
              </CallProvider>
            </PrivacyProvider>
          </AuthProvider>
        </PublicModeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
